/*
 * paragraphUtils — Pure Helper fuer Paragraph-Transformationen
 *
 * Hier leben Funktionen, die Paragraph-Listen transformieren, ohne Zugriff
 * auf Store, Editor oder andere Singletons zu brauchen. Pure Functions,
 * einfach testbar, keine Seiteneffekte.
 */

import { generateUUID } from './uuid.js'

/**
 * Generiert neue UUIDs fuer alle Paragraphen und mappt Section-Links (targetId)
 * konsistent um. Wird beim CBL-Load, duplicateProject und der localStorage-
 * Migration verwendet — jedes Mal wenn eine Paragraph-Liste als "neue Kopie"
 * in die Datenbank wandern soll, damit keine ID-Kollisionen mit bestehenden
 * Paragraphen entstehen (weder lokal noch server-seitig).
 *
 * Format-Agnostisch: funktioniert sowohl fuer Editor-/CBL-Paragraphen (Links
 * direkt auf der Wurzel — `p.links`) als auch fuer Store-Format-Paragraphen
 * (Links unter `p.data.links`). Wird ueber die `linksPath`-Option gesteuert.
 *
 * @param {Object[]} paragraphs - Paragraph-Liste (wird nicht mutiert)
 * @param {Object} [options]
 * @param {'root'|'data'} [options.linksPath='root'] - Wo liegen die Links?
 *        'root' fuer Editor/CBL-Format (p.links), 'data' fuer Store-Format (p.data.links)
 * @returns {{paragraphs: Object[], idMap: Map<string, string>}}
 *          Deep-geclonte Paragraphen mit neuen IDs + Mapping alte→neue ID
 */
export function remapParagraphIds(paragraphs, options = {}) {
    const linksPath = options.linksPath || 'root'

    // Schritt 1: Deep Clone + neue IDs. Die idMap faengt alte→neue Zuordnung ein,
    // damit Section-Links im zweiten Pass konsistent umgeschrieben werden koennen.
    const idMap = new Map()
    const cloned = paragraphs.map(p => {
        const newId = generateUUID()
        if (p.id != null) idMap.set(p.id, newId)
        const copy = JSON.parse(JSON.stringify(p))
        copy.id = newId
        return copy
    })

    // Schritt 2: Section-Links umschreiben. Link-targetIds, die auf andere
    // Paragraphen in derselben Liste verweisen, werden via idMap gemappt.
    // Fremde targetIds (nicht in idMap) bleiben unveraendert — der Broken-Link-
    // Renderer im Editor fangt sie dann als EDITOR_LINK_BROKEN ab.
    for (const p of cloned) {
        const linksContainer = linksPath === 'data' ? p.data : p
        if (!linksContainer || !linksContainer.links) continue

        for (const field of ['text', 'note', 'info']) {
            const fieldLinks = linksContainer.links[field]
            if (!fieldLinks) continue
            linksContainer.links[field] = fieldLinks.map(link => ({
                ...link,
                targetId: idMap.get(link.targetId) || link.targetId
            }))
        }
    }

    return { paragraphs: cloned, idMap }
}

/**
 * FNV-1a-32 Hash fuer Strings. Liefert einen 32-bit unsigned Integer als Number.
 *
 * Wird vom ProjectManager genutzt, um beim Flush Paragraphen-Content-Aenderungen
 * praezise zu erkennen: Gleicher Hash = kein Write, kein Version-Bump, kein
 * Sync-Push. Das verhindert, dass ein Undo (das alle Paragraphen als dirty
 * markiert, obwohl sich nur 1-2 wirklich aendern) Tausende unnoetige IDB-Writes
 * ausloest.
 *
 * FNV-1a-32 ist extrem schnell (~1-2 µs pro Paragraph), deterministisch und
 * kollisionsarm genug fuer ein Best-Effort-Cache (~1/4·10⁹ Kollisionsrate).
 *
 * @param {string} str
 * @returns {number} 32-bit unsigned
 */
export function hashString(str) {
    let hash = 0x811c9dc5  // FNV offset basis
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i)
        // Math.imul fuer 32-bit-Multiplikation, >>> 0 fuer unsigned-Konvertierung
        hash = Math.imul(hash, 0x01000193)
    }
    return hash >>> 0
}

/**
 * Deterministic JSON.stringify mit rekursiv sortierten Object-Keys.
 *
 * Warum nicht `JSON.stringify`? Paragraph-Daten durchlaufen auf dem Cloud-
 * Sync-Pfad PostgreSQL JSONB, das die Key-Reihenfolge intern neu arrangiert
 * (Lesson 43). Ein Paragraph, der lokal als `{text, type, ...}` serialisiert
 * wurde, kommt vom Server als `{type, text, ...}` zurueck — inhaltlich
 * identisch, aber mit abweichender Stringify-Ausgabe und damit abweichendem
 * Hash. Ohne Normalisierung waere der Hash-Filter nach jedem Remote-Roundtrip
 * fragil: Phantom-Writes, steigende Versionen, im schlimmsten Fall Konflikte.
 *
 * Der Helper ist O(n log n) in der Schluesselanzahl pro Node — fuer typische
 * Paragraph-Payloads (~10 Top-Level-Keys, flache Arrays) vernachlaessigbar.
 *
 * @param {*} value
 * @returns {string}
 */
function stableStringify(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value)
    if (Array.isArray(value)) {
        return '[' + value.map(stableStringify).join(',') + ']'
    }
    const keys = Object.keys(value).sort()
    const parts = []
    for (const k of keys) {
        const v = value[k]
        if (v === undefined) continue  // JSON.stringify-Semantik beibehalten
        parts.push(JSON.stringify(k) + ':' + stableStringify(v))
    }
    return '{' + parts.join(',') + '}'
}

/**
 * Hash fuer ein serialisiertes Paragraph-Data-Objekt (aus serializeParagraphRT).
 *
 * Nutzt `stableStringify`, damit der Hash invariant gegenueber Key-
 * Reordering ist (siehe oben: PostgreSQL JSONB Roundtrip).
 *
 * @param {Object} data - serialisiertes Paragraph-Data-Objekt
 * @returns {number} 32-bit FNV-1a Hash
 */
export function hashParagraphData(data) {
    return hashString(stableStringify(data))
}

/**
 * Sortiert server-seitig eingefuegte Absaetze anker-relativ in die lokale
 * Order ein (Lesson 102). Wird vom Pull-Pfad gerufen, wenn der Adopt-Gate
 * (Lesson 100) die Server-Order NICHT uebernimmt, der Pull aber Absaetze
 * geliefert hat, die die lokale Order gar nicht kennt — z.B. vom
 * Memory-Extraktor direkt in Postgres angelegte Project-Memory-Absaetze.
 * Ohne Einsortierung wuerde die loadProject-Toleranz sie ans Dokumentende
 * haengen, und der naechste Save wuerde diese falsche Position pushen.
 *
 * Anker-Logik: jede unbekannte ID wird hinter den naechsten Vorgaenger aus
 * der Server-Order eingefuegt, der lokal schon vorkommt. Die IDs werden in
 * Server-Reihenfolge verarbeitet, damit zusammenhaengende Bloecke ihre
 * Sequenz behalten (M2 ankert an das frisch eingefuegte M1). Ohne Anker
 * (Server-Position 0 ohne gemeinsamen Vorgaenger) landet die ID am Anfang.
 *
 * @param {string[]} localOrder - lokale paragraphOrder (wird nicht mutiert)
 * @param {string[]} pulledIds - IDs der frisch gepullten Absaetze
 * @param {string[]} serverOrder - Server-Order als Positions-Quelle
 * @returns {string[]|null} neue Order, oder null wenn nichts einzusortieren war
 */
export function insertPulledIdsByServerAnchor(localOrder, pulledIds, serverOrder) {
    const known = new Set(localOrder)
    const serverPos = new Map(serverOrder.map((id, i) => [id, i]))
    const toPlace = [...new Set(pulledIds)]
        .filter(id => !known.has(id) && serverPos.has(id))
        .sort((a, b) => serverPos.get(a) - serverPos.get(b))
    if (toPlace.length === 0) return null

    const out = [...localOrder]
    for (const id of toPlace) {
        let insertAt = 0
        for (let j = serverPos.get(id) - 1; j >= 0; j--) {
            const anchorIdx = out.indexOf(serverOrder[j])
            if (anchorIdx >= 0) {
                insertAt = anchorIdx + 1
                break
            }
        }
        out.splice(insertAt, 0, id)
    }
    return out
}
